import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { Copy, KeyRound, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { ConfirmAction } from '@/components/confirm-action'
import { DataTable } from '@/components/data-table'
import { Notice, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  createTokenSchema,
  MAX_TOKEN_DAYS,
  useApiTokens,
  useCreateApiToken,
  useRevokeApiToken,
  type ApiToken,
  type CreateTokenInput,
  type CreatedApiToken,
} from '@/features/api-tokens/api'
import { localDate, localStamp } from '@/lib/datetime'

const STATUS_VARIANT = { active: 'success', expired: 'default', revoked: 'warning' } as const

/**
 * API tokens — Super Admin only. Lets the web repo's i18n scripts call the
 * catalog from CI without anybody's session cookie. See docs/api-contract.md
 * § API tokens for what a token can and cannot reach.
 */
export function ApiTokensPage() {
  const query = useApiTokens()
  const revoke = useRevokeApiToken()
  const [creating, setCreating] = useState(false)
  const [pendingRevoke, setPendingRevoke] = useState<ApiToken | null>(null)

  const columns = useMemo<ColumnDef<ApiToken, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Token',
        cell: ({ row }) => (
          <div>
            <div className="font-semibold">{row.original.name}</div>
            <code className="font-mono text-[11px] text-muted-foreground">
              {row.original.token_prefix}…
            </code>
          </div>
        ),
      },
      {
        accessorKey: 'owner_name',
        header: 'Acts as',
        cell: ({ row }) => (
          <div>
            <div>{row.original.owner_name || row.original.owner_public_id}</div>
            <div className="text-xs text-faint">
              scope: {row.original.scope} · created by{' '}
              {row.original.created_by_name || row.original.created_by}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANT[row.original.status]}>{row.original.status}</Badge>
        ),
      },
      {
        accessorKey: 'expires_at',
        header: 'Expires',
        cell: ({ row }) => localDate(row.original.expires_at),
      },
      {
        accessorKey: 'last_used_at',
        header: 'Last used',
        cell: ({ row }) =>
          row.original.last_used_at ? (
            localStamp(row.original.last_used_at)
          ) : (
            <span className="text-xs italic text-muted-foreground">Never</span>
          ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.status === 'revoked' ? null : (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                disabled={revoke.isPending}
                onClick={() => setPendingRevoke(row.original)}
              >
                Revoke
              </Button>
            </div>
          ),
      },
    ],
    [revoke.isPending],
  )

  return (
    <>
      <PageHeader
        title="API Tokens"
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            New token
          </Button>
        }
      />

      <Notice>
        <b>For scripts, not people.</b> A token acts <b>as its owner</b> — that person&apos;s role
        decides what it may change, and audit rows name them, marked as made through the token — but only on the Localization API
        (<code>/i18n/*</code>). It cannot open any other screen&apos;s API, and it cannot create or
        revoke tokens. If the owner loses their staff role the token stops working at once. The
        secret is shown <b>once</b>, when you create it; only a hash is kept.
      </Notice>

      <Card>
        <DataTable columns={columns} query={query} emptyMessage="No API tokens yet." />
      </Card>

      <CreateTokenDialog open={creating} onOpenChange={setCreating} />

      <ConfirmAction
        open={pendingRevoke !== null}
        onOpenChange={(open) => !open && setPendingRevoke(null)}
        title={`Revoke ${pendingRevoke?.name}?`}
        description={
          <>
            The next request carrying <code>{pendingRevoke?.token_prefix}…</code> is refused. Any CI
            job using it fails until it is given a new token. This cannot be undone; the token stays
            listed as revoked.
          </>
        }
        confirmLabel="Revoke"
        destructive
        onConfirm={async () => {
          if (pendingRevoke) await revoke.mutateAsync(pendingRevoke.id)
        }}
        onConflict={() => void query.refetch()}
      />
    </>
  )
}

function CreateTokenDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const create = useCreateApiToken()
  // Held only in component state, and dropped when the dialog closes. Never
  // written to storage, never logged — the server cannot show it again either.
  const [created, setCreated] = useState<CreatedApiToken | null>(null)
  const form = useForm<CreateTokenInput>({
    resolver: standardSchemaResolver(createTokenSchema),
    defaultValues: { name: '', expires_in_days: 90, owner_public_id: '' },
  })

  function close(next: boolean) {
    // Not while the create request is out: it still makes the token, and
    // create.reset() drops the only callback that would show its secret, so
    // closing here (Cancel, Esc, a click outside) left a live token nobody holds.
    if (!next && create.isPending) return
    if (!next) {
      form.reset()
      create.reset()
      setCreated(null)
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg">
        {created ? (
          <>
            <DialogHeader>
              <DialogTitle>Copy the secret now</DialogTitle>
              <DialogDescription>
                This is the only time it is shown. Put it in the CI secret store; send it as{' '}
                <code>Authorization: Bearer …</code>. If it is lost, revoke this token and create
                another.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2">
              <Input readOnly value={created.secret} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  void navigator.clipboard
                    .writeText(created.secret)
                    .then(() => toast.success('Secret copied.'))
                    .catch(() => toast.error('Could not copy — select the text and copy it by hand.'))
                }}
              >
                <Copy className="size-3" />
                Copy
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Acts as {created.owner_name || created.owner_public_id} · expires{' '}
              {localDate(created.expires_at)}
            </p>
            <DialogFooter>
              <Button type="button" onClick={() => close(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                <KeyRound className="mr-1 inline size-4" />
                New API token
              </DialogTitle>
              <DialogDescription>
                Scope: Localization (<code>/i18n/*</code>) only. Expiry is required.
              </DialogDescription>
            </DialogHeader>

            <form
              id="token-form"
              className="space-y-4"
              onSubmit={form.handleSubmit((values) =>
                create.mutate(values, { onSuccess: (token) => setCreated(token) }),
              )}
            >
              <div className="space-y-2">
                <Label htmlFor="token-name">Name</Label>
                <Input id="token-name" placeholder="web i18n CI" {...form.register('name')} />
                {form.formState.errors.name && (
                  <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="token-days">Expires in (days, 1–{MAX_TOKEN_DAYS})</Label>
                <Input
                  id="token-days"
                  type="number"
                  min={1}
                  max={MAX_TOKEN_DAYS}
                  {...form.register('expires_in_days', { valueAsNumber: true })}
                />
                {form.formState.errors.expires_in_days && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.expires_in_days.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="token-owner">Acts as (staff public id)</Label>
                <Input
                  id="token-owner"
                  placeholder="Leave empty for yourself"
                  {...form.register('owner_public_id')}
                />
                <p className="text-xs text-muted-foreground">
                  Prefer a dedicated Ops Admin account for CI over a person&apos;s own.
                </p>
              </div>
              {create.isError && (
                <p className="text-xs text-destructive">{(create.error as Error).message}</p>
              )}
            </form>

            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                disabled={create.isPending}
                onClick={() => close(false)}
              >
                Cancel
              </Button>
              <Button type="submit" form="token-form" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create token'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
